/**
 * CreateEventType — Step 1 of the event creation wizard.
 *
 * Rewired (fleet#755, 2026-07-09): the tile grid consumes the REAL Event
 * Templates library via useTemplates() → listEventTemplates — the previous
 * screen was baked JSX ("Quarterly Financial Wellness Series", …) that never
 * called the API, so no tenant could ever see relevant templates here. The
 * "Start from a previous event" cards likewise read the real events store
 * (previously three fabricated rows). Loading/empty/error states per the
 * honest-rail doctrine.
 *
 * 2026-07-13 (Greg): removed the "Start from a previous event" section. The
 * three starter templates (Simple RSVP · Open Registration · Private Event)
 * are now REAL entries in the Event Templates library (seeded via
 * listEventTemplates) rather than special quick-create cards, so users can use
 * or clone them like any template. Build from scratch sits at the top, the
 * template library (including the three) below it.
 */

import { useEffect, useState, type ChangeEvent } from "react";
import { alpha, Btn, Chip, Icon, Input, T, tx } from "@v0y4ge/jrni-ui";
import { CreationShell } from "./CreationShell";
import { CreationStore } from "./store";
import { CREATION_CONFIGS } from "./types";
import { WizardCanvas } from "./wizard-utils";
import { useNav } from "../../../shell/router";
import { getAiProviderSettings } from "../../ai/ai-api";
import { useTemplates } from "../../templates/useTemplates";
import type { EventTemplate } from "../../templates/templates-data";
import { useT } from "../../../lib/i18n";

const tmplInitials = (name: string) => name.split(" ").map((p) => p[0]).filter(Boolean).join("").toUpperCase().slice(0, 2) || "T";

/** One line summarising a template's real defaults — only fields it carries. */
function defaultsLine(t: EventTemplate): string {
  const parts: string[] = [];
  if (t.defaults?.capacity) parts.push(`Cap ${t.defaults.capacity}`);
  if (t.defaults?.tickets) parts.push(`${t.defaults.tickets} ticket type${t.defaults.tickets === 1 ? "" : "s"}`);
  if (t.defaults?.comms) parts.push(`${t.defaults.comms} comms`);
  if (t.usage) parts.push(`used ${t.usage}×`);
  return parts.join(" · ");
}

export default function CreateEventType() {
  const t = useT();
  const nav = useNav();
  const { templates, loading, error } = useTemplates();
  // Fresh wizard entry (no `editing` param from the events list, no template
  // session) starts a NEW event — clear any leftover edit/draft data first, so
  // "Create new event" after editing another event doesn't inherit its details.
  useState(() => { if (!nav.params?.editing && !CreationStore.get()._templateMode) CreationStore.reset(); return 0; });
  const [selected, setSelected] = useState<string>(() => CreationStore.get().typeId);
  const [hover, setHover] = useState<string | null>(null);
  const tmplMode = !!(CreationStore.get()._templateMode);
  const [query, setQuery] = useState("");
  const [filterPopular, setFilterPopular] = useState(false);
  // Build with AI is available only once an AI provider is connected in
  // Settings → AI (operator decision 2026-07-16). Fetch the masked settings to
  // decide whether to enable the entry point.
  const [aiConfigured, setAiConfigured] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    getAiProviderSettings()
      .then((r) => { if (live) setAiConfigured(r.settings.status === "configured"); })
      .catch(() => { if (live) setAiConfigured(false); });
    return () => { live = false; };
  }, []);

  const filtered = templates.filter((tp) => {
    if (filterPopular && !tp.popular) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return `${tp.name} ${tp.type} ${tp.desc} ${(tp.tags || []).join(" ")}`.toLowerCase().includes(q);
  });
  const selectedTemplate = templates.find((tp) => tp.id === selected);
  // A previously-seeded BASE TYPE id (returning to this step after picking a
  // template) — keep the current config instead of silently resetting it.
  const baseConfig = selected !== "blank" && selected !== "quick" && !selectedTemplate ? CREATION_CONFIGS[selected] : undefined;

  /** Seed the wizard from a real library template (same interim seeding as the
   *  Event Templates screen's "Use this template"): base type when the template
   *  declares one we know, else blank — plus the template's own label/defaults. */
  const seedFromTemplate = (t: EventTemplate) => {
    if (t.config && Object.keys(t.config).length) {
      // Full-definition snapshot ("Save as template") — apply everything the
      // source event carried (minus dates + registration window).
      CreationStore.applyTemplate(t);
      nav.go("create.basics", { templateId: t.id });
      return;
    }
    if (t.typeId && CREATION_CONFIGS[t.typeId]) CreationStore.setType(t.typeId);
    else CreationStore.setBlank();
    CreationStore.setField("label", t.type || t.name);
    if (t.defaults?.capacity) CreationStore.setField("capacity", t.defaults.capacity);
    if (t.hue) CreationStore.setField("heroTo", t.hue);
    nav.go("create.basics", { templateId: t.id });
  };

  const proceed = () => {
    const cur = CreationStore.get();
    if (selectedTemplate) { seedFromTemplate(selectedTemplate); return; }
    if (baseConfig) {
      if (cur.typeId !== selected) CreationStore.setType(selected);
      nav.go("create.basics"); return;
    }
    if (cur.typeId !== "blank") CreationStore.setBlank();
    nav.go("create.basics");
  };

  const continueLabel =
    selectedTemplate ? `Use ${selectedTemplate.name}` :
    baseConfig ? `Continue with ${baseConfig.label}` : "Build from scratch";
  const selectionLabel =
    selectedTemplate ? selectedTemplate.name :
    baseConfig ? baseConfig.label :
    selected === "blank" ? "Build from scratch" : null;

  return (
    <CreationShell step={0} footer={
      <>
        <Btn variant="secondary" icon={<Icon.arrow dir="left" size={14} />} onClick={() => nav.go("events")}>{t("common.actions.back", "Back")}</Btn>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {selectionLabel ? <span style={{ fontSize: 13, color: T.soft }}><strong style={{ color: T.n1 }}>{selectionLabel}</strong> {t("events.creationCreateEventType.selected", "selected")}</span> : null}
          <Btn iconRight={<Icon.arrow size={14} />} onClick={proceed}>{continueLabel}</Btn>
        </div>
      </>
    }>
      <WizardCanvas width="wide">
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ ...tx.h2, fontWeight: 700 }}>{tmplMode ? t('events.creationCreateEventType.text') : t('events.creationCreateEventType.text2')}</h2>
          <p style={{ ...tx.help, marginTop: 6 }}>{tmplMode ? t('events.creationCreateEventType.text3') : t('events.creationCreateEventType.text4')}</p>
        </div>

        {/* Build from scratch + Build with AI — always the first options */}
        <div style={{ marginBottom: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div role="button" tabIndex={0} onClick={() => setSelected("blank")} onDoubleClick={() => { CreationStore.setBlank(); nav.go("create.basics"); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected("blank"); } }}
            style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderRadius: 10, background: T.white, border: `${selected === "blank" ? 2 : 1.5}px solid ${selected === "blank" ? T.p1 : T.borderStrong}`, boxShadow: selected === "blank" ? `0 0 0 4px ${T.p4}` : "none", cursor: "pointer" }}>
            <span style={{ width: 40, height: 40, borderRadius: 10, background: selected === "blank" ? T.p3 : T.n6, color: selected === "blank" ? T.p1 : T.n1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.plus size={18} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{t("events.creationCreateEventType.buildFromScratch", "Build from scratch")}</div>
              <div style={{ fontSize: 13, color: T.soft, marginTop: 2 }}>{t("events.creationCreateEventType.startWithABlankEventAndConfigureEveryStepYourselfNoTemplate", "Start with a blank event and configure every step yourself — no template defaults.")}</div>
            </div>
            {selected === "blank" ? <Icon.check size={18} /> : <Icon.arrow size={16} />}
          </div>

          {(() => {
            const enabled = aiConfigured === true;
            const onOpen = () => { if (enabled) nav.go("create.ai"); };
            return (
              <div role="button" tabIndex={enabled ? 0 : -1} aria-disabled={!enabled}
                onClick={onOpen}
                onKeyDown={(e) => { if (enabled && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(); } }}
                title={enabled ? t("events.createEventType.describeYourEventAndLetAiDraftIt", "Describe your event and let AI draft it") : t("events.createEventType.connectAnAiProviderInSettingsAiToEnableThis", "Connect an AI provider in Settings → AI to enable this")}
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderRadius: 10, background: enabled ? T.white : T.n6, border: `1.5px solid ${enabled ? T.borderStrong : T.border}`, cursor: enabled ? "pointer" : "not-allowed", opacity: enabled ? 1 : 0.72 }}>
                <span style={{ width: 40, height: 40, borderRadius: 10, background: enabled ? alpha(T.p1, "18") : T.n6, color: enabled ? T.p1 : T.n3, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.star size={18} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: enabled ? T.n1 : T.n2, display: "flex", alignItems: "center", gap: 8 }}>{t("events.creationCreateEventType.buildWithAi", "Build with AI")}</div>
                  <div style={{ fontSize: 13, color: T.soft, marginTop: 2 }}>
                    {aiConfigured === false
                      ? t("events.createEventType.connectAnAiProviderInSettingsAiToTurnAPromptInto", "Connect an AI provider in Settings → AI to turn a prompt into a draft.")
                      : t("events.createEventType.describeYourEventInPlainLanguageAiDraftsItYouReviewAnd", "Describe your event in plain language — AI drafts it, you review and edit.")}
                  </div>
                </div>
                {enabled ? <Icon.arrow size={16} /> : <Icon.lock size={15} />}
              </div>
            );
          })()}
        </div>

        {/* Template library — search + filter */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <Input
            full={false}
            size="sm"
            iconLeft={<Icon.search size={14} />}
            iconRight={query ? <span onClick={() => setQuery("")} style={{ background: "transparent", cursor: "pointer", color: T.soft, display: "inline-flex" }}><Icon.x size={12} /></span> : null}
            value={query}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            placeholder={t("events.creationCreateEventType.searchYourTemplates", "Search your templates…")}
            style={{ flex: 1, minWidth: 240, maxWidth: 360 }}
          />
          <Chip selected={filterPopular} onClick={() => setFilterPopular(!filterPopular)} leading={<Icon.star size={11} />}>{t("events.creationCreateEventType.popularOnly", "Popular only")}</Chip>
          {!loading ? <span style={{ marginLeft: "auto", fontSize: 13, color: T.soft }}>{t("events.creationCreateEventType.showing", "Showing")}{" "}{filtered.length} {t("events.creationCreateEventType.of", "of")}{" "}{templates.length}</span> : null}
        </div>

        {loading ? (
          <div style={{ padding: 40, border: `1px dashed ${T.n4}`, borderRadius: 8, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("events.creationCreateEventType.loadingYourTemplates", "Loading your templates…")}</div>
        ) : error && templates.length === 0 ? (
          <div style={{ padding: 40, border: `1px dashed ${T.n4}`, borderRadius: 8, textAlign: "center", color: T.soft }}>
            <Icon.warn size={20} style={{ color: T.red1 }} />
            <div style={{ marginTop: 8, fontSize: 14, color: T.n1, fontWeight: 600 }}>{t("events.creationCreateEventType.couldnTLoadYourTemplates", "Couldn't load your templates")}</div>
            <div style={{ marginTop: 4, fontSize: 13 }}>{t("events.creationCreateEventType.youCanStillBuildFromScratchOrRetryInAMoment", "You can still build from scratch, or retry in a moment.")}</div>
          </div>
        ) : templates.length === 0 ? (
          <div style={{ padding: 40, border: `1px dashed ${T.n4}`, borderRadius: 8, textAlign: "center", color: T.soft }}>
            <Icon.copy size={20} />
            <div style={{ marginTop: 8, fontSize: 14, color: T.n1, fontWeight: 600 }}>{t("events.creationCreateEventType.noTemplatesYet", "No templates yet")}</div>
            <div style={{ marginTop: 4, fontSize: 13 }}>{t("events.creationCreateEventType.buildFromScratchAboveOrCreateAReusableTemplateUnderEventsEvent", "Build from scratch above, or create a reusable template under Events · Event Templates.")}</div>
            <Btn variant="secondary" size="sm" style={{ marginTop: 12 }} onClick={() => nav.go("templates")}>{t("events.creationCreateEventType.openEventTemplates", "Open Event Templates")}</Btn>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, border: `1px dashed ${T.n4}`, borderRadius: 8, textAlign: "center", color: T.soft }}>
            <Icon.search size={20} />
            <div style={{ marginTop: 8, fontSize: 14, color: T.n1, fontWeight: 600 }}>{t("events.creationCreateEventType.noTemplatesMatch", "No templates match \"")}{" "}{query}"</div>
            <Btn variant="ghost" size="sm" style={{ marginTop: 12 }} onClick={() => { setQuery(""); setFilterPopular(false); }}>{t("events.creationCreateEventType.clearFilters", "Clear filters")}</Btn>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {filtered.map((tp) => {
              const isOn = selected === tp.id; const isHover = hover === tp.id;
              const hue = tp.hue || T.p1;
              const meta = defaultsLine(tp);
              return (
                <div key={tp.id}
                  onClick={() => setSelected(tp.id)}
                  onDoubleClick={() => seedFromTemplate(tp)}
                  onMouseEnter={() => setHover(tp.id)} onMouseLeave={() => setHover(null)}
                  style={{ padding: 16, borderRadius: 8, background: T.white, border: `${isOn ? 2 : 1}px solid ${isOn ? T.p1 : (isHover ? T.borderStrong : T.border)}`, boxShadow: isOn ? `0 0 0 4px ${T.p4}` : (isHover ? T.shadowChrome : "none"), cursor: "pointer", display: "flex", flexDirection: "column", gap: 8, minHeight: 132, position: "relative", transition: "border-color 120ms, box-shadow 120ms" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", alignItems: "stretch", gap: 8, height: 36 }}>
                      <span style={{ width: 36, height: 36, borderRadius: 8, background: alpha(hue, "22"), color: hue, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 13, fontWeight: 800 }}>{tmplInitials(tp.name)}</span>
                      {tp.popular ? <span style={{ display: "inline-flex", alignItems: "center", padding: "0 10px", height: 36, borderRadius: 8, background: T.p3, color: T.p1, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{t("events.creationCreateEventType.popular", "Popular")}</span> : null}
                      {tp.status === "draft" ? <span style={{ display: "inline-flex", alignItems: "center", padding: "0 10px", height: 36, borderRadius: 8, background: T.n6, color: T.n2, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{t("common.status.draft", "Draft")}</span> : null}
                    </div>
                    {isOn ? <Icon.check size={16} /> : null}
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.n1 }}>{tp.name}</div>
                    <div style={{ fontSize: 12, color: T.soft, marginTop: 4, lineHeight: 1.4 }}>{tp.type}{meta ? t('events.creationCreateEventType.text5', { meta }) : ""}</div>
                  </div>
                  {isHover && (tp.desc || meta) ? (
                    <div style={{ position: "absolute", top: "calc(100% + 8px)", left: -8, right: -8, background: T.n1, color: T.white, padding: "14px 16px", borderRadius: 8, boxShadow: T.shadowLg, zIndex: 20, fontFamily: T.font, pointerEvents: "none", animation: "tipFadeIn 140ms cubic-bezier(0.4,0,0.2,1)" }}>
                      <div style={{ position: "absolute", top: -5, left: 24, width: 10, height: 10, background: T.n1, transform: "rotate(45deg)" }} />
                      {tp.desc ? <div style={{ fontSize: 13, lineHeight: 1.55, color: "rgba(255,255,255,0.92)" }}>{tp.desc}</div> : null}
                      {meta ? <div style={{ fontSize: 11, color: hue, fontWeight: 600, marginTop: tp.desc ? 10 : 0, textTransform: "uppercase", letterSpacing: 0.04, display: "flex", alignItems: "center", gap: 6 }}><Icon.info size={11} /> {meta}{tp.lastEdit ? /* TODO(i18n): replace template-literal with t('events.creationCreateEventType.text6', { ... }) */` · edited ${tp.lastEdit}` : ""}</div> : null}
                      <style>{t('events.creationCreateEventType.text7')}</style>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </WizardCanvas>
    </CreationShell>
  );
}
